import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatCount } from "@shared-core/domain/formatters";
import type { ListResponse } from "@shared-core/data/adapter";
import type { KitDropView } from "@shared-core/data/customer-link-adapter";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import { ParcelTracking } from "./ParcelTracking";

// Where a kit goes (V1 completion R5): a kit is up to 2,000 identical packs to as many places, so the order page
// counts them in one sentence and reads the places a page at a time, searched on the server, instead of listing each
// as a delivery. The read hands out the URL (`drops_url`) and each door answers it with its own credential.

const PAGE_SIZE = 10;
const SEARCH_DELAY_MS = 250;

export interface KitPlacesView {
  ref: string;
  title: string;
  /** "250 kits to 250 places · 3 despatched · 1 delivered". */
  summary: string;
  /** Null where this page cannot read the places: the sentence stands alone. */
  dropsUrl: string | null;
}

export type LoadKitDrops = (
  dropsUrl: string,
  options: { q?: string; page?: number; pageSize?: number },
) => Promise<ListResponse<KitDropView>>;

type PlacesState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ok"; result: ListResponse<KitDropView> };

export function KitPlaces({
  kit,
  load,
}: {
  kit: KitPlacesView;
  load: LoadKitDrops | null;
}) {
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState<PlacesState>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  const dropsUrl = load ? kit.dropsUrl : null;

  // A new search starts again at the first page.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(typed.trim());
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => {
    if (!load || !dropsUrl) return;
    let isCurrent = true;
    setState({ kind: "loading" });
    load(dropsUrl, { q: q || undefined, page, pageSize: PAGE_SIZE })
      .then((result) => {
        if (isCurrent) setState({ kind: "ok", result });
      })
      .catch(() => {
        if (isCurrent) setState({ kind: "error" });
      });
    return () => {
      isCurrent = false;
    };
  }, [load, dropsUrl, q, page, attempt]);

  const heading = (
    <div>
      <p className="font-medium">
        <span className="text-muted-foreground tabular-nums">{kit.ref}</span>{" "}
        {kit.title}
      </p>
      <p className="text-sm text-muted-foreground">{kit.summary}</p>
    </div>
  );
  if (!dropsUrl) return <Card className="gap-3 p-5">{heading}</Card>;

  const searchId = `kit-places-search-${kit.ref}`;
  return (
    <Card className="gap-3 p-5" data-testid={`kit-places-${kit.ref}`}>
      {heading}
      <div>
        <label htmlFor={searchId} className="sr-only">
          Search the places for {kit.title}
        </label>
        <Input
          id={searchId}
          type="search"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Search by place or postcode"
        />
      </div>
      {state.kind === "loading" && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading the places...
        </p>
      )}
      {state.kind === "error" && (
        <p role="alert" className="text-sm">
          We couldn&apos;t load the places.{" "}
          <button
            type="button"
            className="font-medium underline underline-offset-2"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Try again
          </button>
        </p>
      )}
      {state.kind === "ok" && state.result.items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {q ? `No place matches "${q}".` : "No places on this kit yet."}
        </p>
      )}
      {state.kind === "ok" && state.result.items.length > 0 && (
        <>
          <ul className="divide-y">
            {state.result.items.map((d) => (
              <li key={d.number} className="space-y-2 py-2.5 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <span>
                    <span className="text-muted-foreground tabular-nums">
                      {d.number}.
                    </span>{" "}
                    <span className="font-medium">{d.place}</span>
                    <span className="block text-muted-foreground">
                      {d.address_lines.join(", ")}
                    </span>
                  </span>
                  <span className="text-right text-muted-foreground">
                    {d.kits > 1 && (
                      <span className="block tabular-nums">
                        {formatCount(d.kits)} kits
                      </span>
                    )}
                    {d.status_label}
                  </span>
                </div>
                <ParcelTracking
                  carrier={d.carrier}
                  trackingReference={d.tracking_reference}
                  trackingUrl={d.tracking_url}
                />
              </li>
            ))}
          </ul>
          <nav
            aria-label={`Places for ${kit.title}`}
            className="flex items-center justify-between gap-2 text-sm"
          >
            <span className="text-muted-foreground tabular-nums">
              {formatCount((state.result.page - 1) * PAGE_SIZE + 1)}-
              {formatCount(
                (state.result.page - 1) * PAGE_SIZE + state.result.items.length,
              )}{" "}
              of {formatCount(state.result.total)}
            </span>
            <span className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={state.result.page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="size-4" />
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={state.result.page >= state.result.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="size-4" />
              </Button>
            </span>
          </nav>
        </>
      )}
    </Card>
  );
}
